import { useKeyframes } from '@/composables/useKeyframes';
import { render } from '@/threejs/core';
import * as THREE from 'three';

/**
 * AnimationManager - Manages Three.js AnimationMixer and keyframe animations
 * Uses Three.js's built-in KeyframeTrack, AnimationClip, and AnimationMixer
 */
class AnimationManager {
  constructor() {
    this.mixer = null;
    this.scene = null;
    this.clips = new Map(); // Map of objectId+key -> AnimationClip
    this.actions = new Map(); // Map of objectId+key -> AnimationAction
    this.isPlaying = false;
    this.currentTime = 0;
    this.duration = 500; // Default timeline duration in seconds
  }

  /**
   * Initialize the animation manager with a scene
   */
  initialize(scene) {
    this.scene = scene;
    // Create a single mixer for the entire scene
    this.mixer = new THREE.AnimationMixer(scene);
  }

  /**
   * Play all animations from current time
   */
  play() {
    this.isPlaying = true;

    // Play all actions
    this.actions.forEach((action, key) => {
      action.paused = false;
      if (!action.isRunning()) {
        action.play();
      }
    });
  }

  /**
   * Pause all animations
   */
  pause() {
    this.isPlaying = false;

    // Pause all actions
    this.actions.forEach((action) => {
      action.paused = true;
    });
  }

  /**
   * Stop all animations and reset to start
   */
  stop() {
    this.isPlaying = false;
    this.currentTime = 0;

    this.actions.forEach((action) => {
      action.stop();
      action.reset();
      const mixer = action.getMixer();
      if (mixer) {
        mixer.setTime(0);
      }
    });

    render();
  }

  togglePlay() {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  updateObjects() {
    const { getAllKeyframeValues } = useKeyframes();

    getAllKeyframeValues(this.currentTime);

    // loop the crux modules
  }

  /**
   * Seek to a specific time
   * @param {number} time - Time in seconds
   */
  setTime(time) {
    this.currentTime = time;

    this.updateObjects();

    render();
  }

  /**
   * Update the animation mixer (call this in your animation loop)
   * @param {number} deltaTime - Time elapsed since last frame in seconds
   * @returns {boolean} - Whether the time changed
   */
  update(deltaTime) {
    if (!this.isPlaying) return false;

    // Always update current time when playing, even if there are no actions
    this.currentTime += deltaTime;

    this.updateObjects();

    // Always return true when playing so the timeline UI updates
    return true;
  }

  /**
   * Get current playback time
   */
  getCurrentTime() {
    return this.currentTime;
  }

  /**
   * Set the total duration of the timeline
   */
  setDuration(duration) {
    this.duration = duration;
  }

  /**
   * Get all animation clips
   */
  getClips() {
    return Array.from(this.clips.values());
  }

  /**
   * Get all animation actions
   */
  getActions() {
    return Array.from(this.actions.values());
  }

  /**
   * Clear all animations
   */
  clear() {
    this.stop();

    // Clear all actions
    this.actions.forEach((action) => {
      const mixer = action.getMixer();
      if (mixer) {
        mixer.uncacheAction(action.getClip());
      }
      action.stop();
    });

    this.actions.clear();
    this.clips.clear();
  }

  /**
   * Dispose of the animation manager
   */
  dispose() {
    this.clear();
    this.mixer = null;
    this.scene = null;
  }
}

// Create singleton instance
export const animationManager = new AnimationManager();
